import useUser from '@hooks/useUser';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { Grid2 as Grid, Typography, Box } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import LoadingPaper from '@atoms/LoadingPaper';
import WrestlerBirthGenderForm from '@components/AddWrestler/WrestlerBirthGenderForm';
import { STATUS, UserAthlete } from '@type/db-types';
import { log } from '@utils/logging';
import { stringFromError } from '@utils/utils';
import { updateUserAthlete } from '@modules/supabase/users';
import SubmitWrestlerFAB from './SubmitWrestlerFAB';
import WrestlerNameForm from '@components/AddWrestler/WrestlerNameForm';
import WrestlerStatsForm from '@components/AddWrestler/WrestlerStatsForm';
import WrestlerEmailPhoneForm from '@components/AddWrestler/WrestlerEmailPhoneForm';
import { hidePlaceholderEmail } from '@utils/shared-utils';

export default function WrestlerDetail() {
    const params = useParams<{ athleteId: string | undefined }>();
    const { athletes, refresh } = useUser();

    // get selectedWrestlerDetail from route
    const athlete = useMemo(() => {
        const a = { ...athletes.find(({ athleteId }) => athleteId === params.athleteId) };
        a.email = hidePlaceholderEmail(a?.email);
        return a;
    }, [athletes, params.athleteId]);

    const formMethods = useForm<UserAthlete>({
        defaultValues: athlete,
        mode: 'all',
    });

    useEffect(() => {
        if (athlete) formMethods.reset(athlete);
    }, [athlete, formMethods]);

    const onSubmit: SubmitHandler<UserAthlete> = useCallback(
        async (values) => {
            updateUserAthlete(values)
                .catch((e) => log(`[Wrestlers:edit] ${stringFromError(e)}`))
                .then(refresh);
        },
        [refresh],
    );

    return athlete ? (
        <LoadingPaper elevation={2} sx={{ padding: '1rem', margin: '1rem', elevation: 2 }}>
            <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit(onSubmit)}>
                    <Box sx={{ padding: 10 }}>
                        <Grid container spacing={5}>
                            <Grid size={12}>
                                <Typography variant="h5">Wrestler Details</Typography>
                            </Grid>

                            <WrestlerNameForm />

                            <WrestlerBirthGenderForm />

                            <WrestlerStatsForm />

                            <WrestlerEmailPhoneForm
                                emailDisabled={
                                    !!athlete.email && athlete.userStatus === STATUS.ACTIVE
                                }
                            />
                        </Grid>
                    </Box>
                    <SubmitWrestlerFAB />
                </form>
            </FormProvider>
        </LoadingPaper>
    ) : (
        <Box sx={{ padding: 10 }}>
            <Typography variant="h4" gutterBottom>
                Wrestler not found
            </Typography>
            <Typography variant="body1">
                The wrestler you are looking for is not found. Please check the URL and try again.
            </Typography>
        </Box>
    );
}
